import {
  type AdvisorEntry,
  type Alias,
  type CommittedDelta,
  type DistributionList,
  type Extent,
  emptyTimeline,
  type Fork,
  type Input,
  isTrack,
  mapAtEnd,
  type Perspective,
  type PerspectiveChangeRequest,
  type PerspectiveSuggestion,
  type PlayerPlace,
  type PollResponse,
  type Properties,
  type PropertiesChanges,
  type ProposingUser,
  reachedViews,
  type Scene,
  type Skill,
  sceneStory,
  speakerKey,
  type Thing,
  type ThreadEntry,
  type ThreadMember,
  type ThreadSpeaker,
  type ThreadStatus,
  type Timeline,
  targetOf,
  type UserDelta,
  type UserHistory,
  type ViewRef,
  type WorldHistory,
  writeTimeline,
} from "@pax/world";

/** What the spectator and every perspective share: the map, and the story so far. */
export interface ViewState {
  /** How the view sees each perspective, by handle. */
  aliases: Record<string, Alias>;
  /**
   * The view's history as the writes it received wrote it, with its labels. Views that received the
   * same writes share one, which is never changed in place, and so do their maps.
   */
  timeline: Timeline;
  /** Every thing on the map, every canvas's, where the timeline ends, as it is there. */
  map: Thing[];
  /** Every scene the view has been shown, in the order they came. */
  scenes: SceneState[];
}

/** A scene a view has been shown, with the stretch of the timeline it shows. */
export interface SceneState {
  id: string;
  title: string;
  /** Its events' stories in turn, each under its header. */
  story: string;
  /** The earliest and the latest tick it shows. */
  ticks: { from: number; to: number };
  /** The revision that showed it. */
  revision: number;
}

/** The earliest and the latest tick a scene shows. */
export function ticksShown(scene: Pick<Scene, "tick">): { from: number; to: number } {
  const ticks = isTrack(scene.tick) ? scene.tick.keys.map(key => key.value) : [scene.tick];
  return { from: Math.min(...ticks), to: Math.max(...ticks) };
}

export interface SpectatorState extends ViewState {
  perspectiveSuggestions: PerspectiveSuggestion[];
}

export interface PollState {
  status: "open" | "closed";
  /** One per respondent; a later response replaces an earlier one. */
  responses: { respondent: ThreadSpeaker; response: PollResponse }[];
}

/** One mark on a message, and everyone who left it, in the order they did. */
export interface ThreadReaction {
  mark: string;
  reactors: ThreadSpeaker[];
}

export interface ThreadState {
  id: string;
  title: string;
  status: ThreadStatus;
  /** What the conversation is about on the map. */
  frame?: Extent;
  members: ThreadMember[];
  entries: ThreadEntry[];
  polls: Record<string, PollState>;
  /** The marks on each message, by entry id, in the order they were first left. */
  reactions: Record<string, ThreadReaction[]>;
}

export interface ActionState {
  id: string;
  text: string;
  status: "pending" | "committed" | "withdrawn";
}

export interface PerspectiveState extends ViewState {
  perspective: Perspective;
  /** The users playing it. While it has any, it receives the world's round deltas. */
  userIds: string[];
  /** Its ask for the next jump. A jump starting drops it. */
  jumpRequest: { guidance?: string } | null;
  /** The input it waits on, until it is answered or withdrawn. */
  awaits: Input | null;
  threads: ThreadState[];
  /** Threads the players keep at the top of their list, in order. */
  pinnedThreadIds: string[];
  actions: ActionState[];
  /** The players' own place for entries, by board and entry. Entries left out keep the board's. */
  boardPlaces: Record<string, Record<string, PlayerPlace>>;
}

export interface UserState {
  /**
   * The user's advisor entries, by the handle of the perspective they were on when each was
   * written. A copy holds only the perspectives it sees, so entries written on others are left out.
   */
  advisor: Record<string, AdvisorEntry[]>;
  /** The user's latest ask to change perspective, until it is approved or withdrawn. */
  perspectiveChange: PerspectiveChangeState | null;
}

export type PerspectiveChangeState =
  | { status: "pending"; request: PerspectiveChangeRequest }
  | { status: "refused"; request: PerspectiveChangeRequest; reason: string };

export interface GameMasterState {
  skills: Skill[];
  distributionLists: DistributionList[];
}

export interface WorldState {
  revision: number;
  /** Rounds are counted from 1; each completed jump opens the next. */
  round: number;
  /** The tick the current round began at, which its label dates. */
  roundTick: number;
  /** The jump under way: the round it ends, and what the players asked of it. */
  activeJump: { round: number; guidance?: string } | null;
  spectator: SpectatorState;
  perspectives: Record<string, PerspectiveState>;
  users: Record<string, UserState>;
  gameMaster: GameMasterState;
  /** In a player's copy, what their perspective was forked with, until its `perspective.set`. */
  forkCopy?: ViewState;
}

export function emptyWorldState(): WorldState {
  return {
    revision: 0,
    round: 1,
    roundTick: 0,
    activeJump: null,
    spectator: {
      aliases: {},
      timeline: emptyTimeline(),
      map: [],
      scenes: [],
      perspectiveSuggestions: [],
    },
    perspectives: {},
    users: {},
    gameMaster: { skills: [], distributionLists: [] },
  };
}

/** Every ask to change perspective the game master has not answered, oldest first. */
export function pendingPerspectiveChanges(state: WorldState): PerspectiveChangeRequest[] {
  return Object.values(state.users)
    .flatMap(user =>
      user.perspectiveChange?.status === "pending" ? [user.perspectiveChange.request] : [],
    )
    .sort((left, right) => left.revision - right.revision);
}

/** Reduces the full record, or a player's copy read as the player. */
export function reduceWorld(
  record: WorldHistory | UserHistory,
  reader?: ProposingUser,
): WorldState {
  const state = emptyWorldState();
  for (const delta of record) applyDelta(state, delta, reader);
  return state;
}

/**
 * A copy of the world that shares its timelines and maps: each write replaces them rather than
 * editing them, and the geometry in them is most of a world's size.
 */
export function copyWorld(state: WorldState): WorldState {
  const { spectator, perspectives, ...rest } = state;
  return {
    ...structuredClone(rest),
    spectator: copyView(spectator),
    perspectives: Object.fromEntries(
      Object.entries(perspectives).map(([id, view]) => [id, copyView(view)]),
    ),
  };
}

function copyView<View extends ViewState>(view: View): View {
  const { timeline, map, ...rest } = view;
  return { ...structuredClone(rest), timeline, map } as View;
}

/**
 * What a new perspective holds: nothing but a copy of the timeline, map, aliases, and scenes of the
 * view it forks. In a player's copy they arrived as copied deltas, gathered in `forkCopy`.
 */
function startingView(
  state: WorldState,
  fork: Fork | "copy" | undefined,
): Omit<PerspectiveState, "perspective" | "userIds"> {
  const forked =
    fork === "copy"
      ? state.forkCopy
      : fork?.type === "spectator"
        ? state.spectator
        : Object.values(state.perspectives).find(view => view.perspective.handle === fork?.handle);
  const { aliases, timeline, map, scenes } = forked
    ? copyView(forked)
    : { aliases: {}, timeline: emptyTimeline(), map: [], scenes: [] };
  return {
    aliases,
    timeline,
    map,
    scenes,
    jumpRequest: null,
    awaits: null,
    threads: [],
    pinnedThreadIds: [],
    actions: [],
    boardPlaces: {},
  };
}

/** The spectator and every perspective with users. */
function activeViews(state: WorldState): ViewRef[] {
  return [
    { type: "spectator" },
    ...Object.entries(state.perspectives)
      .filter(([, view]) => view.userIds.length > 0)
      .map(([id]) => ({ type: "perspective" as const, handle: id })),
  ];
}

/**
 * A perspective's view. In a player's copy, the deltas copied from the views their perspective was
 * forked from come before its `perspective.set`, so they gather in `forkCopy` until it arrives.
 */
function viewOf(state: WorldState, id: string, reader: ProposingUser | undefined): ViewState[] {
  const view = state.perspectives[id];
  if (view) return [view];
  if (id !== reader?.perspectiveHandle) return [];
  state.forkCopy ??= { aliases: {}, timeline: emptyTimeline(), map: [], scenes: [] };
  return [state.forkCopy];
}

/** Applies one committed delta to the state in place. A player's copy is read as the player. */
export function applyDelta(
  state: WorldState,
  delta: CommittedDelta | UserDelta,
  reader?: ProposingUser,
): void {
  state.revision = delta.revision;
  const { operation } = delta;
  const target = targetOf(delta, reader);
  const reached =
    target.type === "perspectives"
      ? reachedViews(
          target.perspectives,
          listId => state.gameMaster.distributionLists.find(list => list.id === listId)?.handles,
        )
      : target.type === "activeViews"
        ? activeViews(state)
        : [];
  const views: ViewState[] = reached.flatMap(ref =>
    ref.type === "spectator" ? [state.spectator as ViewState] : viewOf(state, ref.handle, reader),
  );
  /** A player's copy holds only their view's rounds; the full record's are the spectator's. */
  const reachesClock = reader !== undefined || reached.some(ref => ref.type === "spectator");
  const perspectives: PerspectiveState[] = reached.flatMap(ref =>
    ref.type === "perspective" ? (state.perspectives[ref.handle] ?? []) : [],
  );
  const { gameMaster } = state;

  switch (operation.type) {
    case "perspective.set": {
      const { perspective, fork } = operation;
      state.perspectives[perspective.handle] = {
        ...startingView(state, perspective.handle === reader?.perspectiveHandle ? "copy" : fork),
        perspective,
        userIds: [],
      };
      delete state.forkCopy;
      return;
    }
    case "perspective.update":
      for (const view of perspectives) {
        view.perspective = applyChanges(view.perspective, operation.changes);
      }
      return;
    case "perspective.updateUsers":
      for (const view of perspectives) {
        view.userIds = [...operation.userIds];
        if (operation.userIds.length === 0) view.jumpRequest = null;
      }
      return;
    case "perspective.unset":
      for (const { perspective } of perspectives) {
        delete state.perspectives[perspective.handle];
        gameMaster.distributionLists = gameMaster.distributionLists.map(list => ({
          ...list,
          handles: list.handles.filter(handle => handle !== perspective.handle),
        }));
      }
      return;
    case "distributionList.set":
      gameMaster.distributionLists.push(operation.distributionList);
      return;
    case "distributionList.update":
      gameMaster.distributionLists = gameMaster.distributionLists.map(list =>
        list.id === operation.id ? applyChanges(list, operation.changes) : list,
      );
      return;
    case "distributionList.perspectiveAdded":
      gameMaster.distributionLists = gameMaster.distributionLists.map(list =>
        list.id === operation.id && !list.handles.includes(operation.handle)
          ? { ...list, handles: [...list.handles, operation.handle] }
          : list,
      );
      return;
    case "distributionList.perspectiveRemoved":
      gameMaster.distributionLists = gameMaster.distributionLists.map(list =>
        list.id === operation.id
          ? {
              ...list,
              handles: list.handles.filter(handle => handle !== operation.handle),
            }
          : list,
      );
      return;
    case "distributionList.unset":
      gameMaster.distributionLists = gameMaster.distributionLists.filter(
        list => list.id !== operation.id,
      );
      return;
    case "perspectiveSuggestion.set":
      state.spectator.perspectiveSuggestions.push(operation.perspectiveSuggestion);
      return;
    case "perspectiveSuggestion.update":
      state.spectator.perspectiveSuggestions = state.spectator.perspectiveSuggestions.map(
        suggestion =>
          suggestion.id === operation.id ? applyChanges(suggestion, operation.changes) : suggestion,
      );
      return;
    case "perspectiveSuggestion.unset":
      state.spectator.perspectiveSuggestions = state.spectator.perspectiveSuggestions.filter(
        suggestion => suggestion.id !== operation.id,
      );
      return;
    case "perspectiveChange.requested":
    case "perspectiveChange.withdrawn":
    case "perspectiveChange.approved":
    case "perspectiveChange.refused": {
      if (target.type !== "users") return;
      for (const userId of target.userIds) {
        const user = userState(state, userId);
        const current = user.perspectiveChange;
        if (operation.type === "perspectiveChange.requested") {
          user.perspectiveChange = {
            status: "pending",
            request: { id: operation.id, userId, to: operation.to, revision: delta.revision },
          };
        } else if (current?.request.id !== operation.id) {
        } else if (operation.type === "perspectiveChange.refused") {
          user.perspectiveChange = {
            status: "refused",
            request: current.request,
            reason: operation.reason,
          };
        } else {
          user.perspectiveChange = null;
        }
      }
      return;
    }
    case "alias.set":
      for (const view of views) view.aliases[operation.handle] = operation.alias;
      return;
    case "alias.unset":
      for (const view of views) delete view.aliases[operation.handle];
      return;
    case "timeline.extended":
    case "timeline.clarified": {
      const written = new Map<Timeline, { timeline: Timeline; map: Thing[] }>();
      for (const view of views) {
        let next = written.get(view.timeline);
        if (!next) {
          const timeline = writeTimeline(view.timeline, operation);
          next = { timeline, map: mapAtEnd(timeline) };
          written.set(view.timeline, next);
        }
        view.timeline = next.timeline;
        view.map = next.map;
      }
      return;
    }
    case "scene.shown": {
      const { scene } = operation;
      for (const view of views) {
        view.scenes.push({
          id: scene.id,
          title: scene.title,
          story: sceneStory(scene),
          ticks: ticksShown(scene),
          revision: delta.revision,
        });
      }
      return;
    }
    case "input.asked":
      for (const perspective of perspectives) perspective.awaits = operation.input;
      return;
    case "world.started":
      if (!reachesClock) return;
      state.round = operation.roundCompleted + 1;
      state.roundTick = operation.tick;
      return;
    case "input.answered":
    case "input.withdrawn":
      for (const perspective of perspectives) perspective.awaits = null;
      return;
    case "jump.intervened":
      return;
    case "jump.requested":
      for (const perspective of perspectives) {
        perspective.jumpRequest = operation.guidance ? { guidance: operation.guidance } : {};
      }
      return;
    case "jump.requestWithdrawn":
      for (const perspective of perspectives) perspective.jumpRequest = null;
      return;
    case "jump.started":
      for (const perspective of perspectives) {
        perspective.awaits = null;
        perspective.jumpRequest = null;
        for (const action of perspective.actions) {
          if (action.status === "pending") action.status = "committed";
        }
      }
      if (!reachesClock) return;
      state.activeJump = {
        round: operation.round,
        ...(operation.guidance ? { guidance: operation.guidance } : {}),
      };
      return;
    case "jump.completed":
      for (const perspective of perspectives) perspective.awaits = null;
      if (!reachesClock) return;
      state.activeJump = null;
      state.round = operation.roundCompleted + 1;
      state.roundTick = operation.tick;
      return;
    case "thread.create":
      for (const perspective of perspectives) {
        perspective.threads.push({
          ...operation.thread,
          members: [...operation.thread.members],
          status: "open",
          entries: [],
          polls: {},
          reactions: {},
        });
      }
      return;
    case "thread.append":
      for (const perspective of perspectives) {
        const thread = perspective.threads.find(thread => thread.id === operation.threadId);
        if (!thread) continue;
        thread.entries.push(operation.entry);
        const { content, presentation } = operation.entry;
        if (content.type === "poll")
          thread.polls[content.pollId] = { status: "open", responses: [] };
        if (
          presentation.type === "perspective" &&
          perspective.awaits?.type === "reply" &&
          perspective.awaits.threadId === thread.id
        ) {
          perspective.awaits = null;
        }
      }
      return;
    case "thread.update":
      for (const perspective of perspectives) {
        const thread = perspective.threads.find(thread => thread.id === operation.id);
        if (!thread) continue;
        const { title, status, frame, members } = operation.changes;
        if (title !== undefined) thread.title = title;
        if (status !== undefined) thread.status = status;
        if (frame === null) delete thread.frame;
        else if (frame !== undefined) thread.frame = frame;
        for (const change of members ?? []) {
          if (change.type === "append") {
            thread.members = [
              ...thread.members.filter(member => member.id !== change.member.id),
              change.member,
            ];
          } else if (change.type === "update") {
            thread.members = thread.members.map(member =>
              member.id === change.id ? applyChanges(member, change.changes) : member,
            );
          } else {
            thread.members = thread.members.filter(member => member.id !== change.id);
          }
        }
      }
      return;
    case "thread.pollResponded":
      for (const perspective of perspectives) {
        const poll = perspective.threads.find(thread => thread.id === operation.threadId)?.polls[
          operation.pollId
        ];
        if (!poll) continue;
        const key = speakerKey(operation.respondent);
        poll.responses = poll.responses
          .filter(existing => speakerKey(existing.respondent) !== key)
          .concat({ respondent: operation.respondent, response: operation.response });
      }
      return;
    case "thread.reactionSet":
    case "thread.reactionUnset":
      for (const perspective of perspectives) {
        const thread = perspective.threads.find(thread => thread.id === operation.threadId);
        if (!thread) continue;
        const marks = thread.reactions[operation.entryId] ?? [];
        const key = speakerKey(operation.reactor);
        const others = (reaction: ThreadReaction) =>
          reaction.reactors.filter(reactor => speakerKey(reactor) !== key);
        const next =
          operation.type === "thread.reactionSet"
            ? marks.some(reaction => reaction.mark === operation.mark)
              ? marks.map(reaction =>
                  reaction.mark === operation.mark
                    ? { ...reaction, reactors: [...others(reaction), operation.reactor] }
                    : reaction,
                )
              : [...marks, { mark: operation.mark, reactors: [operation.reactor] }]
            : marks
                .map(reaction =>
                  reaction.mark === operation.mark
                    ? { ...reaction, reactors: others(reaction) }
                    : reaction,
                )
                .filter(reaction => reaction.reactors.length > 0);
        if (next.length > 0) thread.reactions[operation.entryId] = next;
        else delete thread.reactions[operation.entryId];
      }
      return;
    case "thread.pollClosed":
      for (const perspective of perspectives) {
        const poll = perspective.threads.find(thread => thread.id === operation.threadId)?.polls[
          operation.pollId
        ];
        if (poll) poll.status = "closed";
      }
      return;
    case "thread.pinsSet":
      for (const perspective of perspectives)
        perspective.pinnedThreadIds = [...operation.threadIds];
      return;
    case "action.submitted":
      for (const perspective of perspectives) {
        perspective.actions.push({ ...operation.action, status: "pending" });
      }
      return;
    case "action.edited":
    case "action.withdrawn":
      for (const perspective of perspectives) {
        const action = perspective.actions.find(action => action.id === operation.id);
        if (!action) continue;
        if (operation.type === "action.edited") action.text = operation.text;
        else action.status = "withdrawn";
      }
      return;
    case "board.arranged":
      for (const perspective of perspectives) {
        perspective.boardPlaces = {
          ...perspective.boardPlaces,
          [operation.canvasId]: operation.places,
        };
      }
      return;
    case "skill.set":
      state.gameMaster.skills.push(operation.skill);
      return;
    case "skill.update":
      state.gameMaster.skills = state.gameMaster.skills.map(skill =>
        skill.id === operation.id ? applyChanges(skill, operation.changes) : skill,
      );
      return;
    case "skill.unset":
      state.gameMaster.skills = state.gameMaster.skills.filter(skill => skill.id !== operation.id);
      return;
    case "advisor.entryAdded":
      if (target.type !== "users") return;
      for (const userId of target.userIds) {
        const handle = handleOf(state, userId);
        if (!handle) continue;
        const { advisor } = userState(state, userId);
        advisor[handle] = [...(advisor[handle] ?? []), operation.entry];
      }
      return;
    case "advisor.cleared":
      if (target.type !== "users") return;
      for (const userId of target.userIds) {
        const handle = handleOf(state, userId);
        if (handle) delete userState(state, userId).advisor[handle];
      }
      return;
  }
}

/** The handle of the perspective a user is on, among those the state holds. */
function handleOf(state: WorldState, userId: string): string | undefined {
  return Object.values(state.perspectives).find(view => view.userIds.includes(userId))?.perspective
    .handle;
}

function userState(state: WorldState, userId: string): UserState {
  const existing = state.users[userId];
  if (existing) return existing;
  const created: UserState = { advisor: {}, perspectiveChange: null };
  state.users[userId] = created;
  return created;
}

/**
 * Applies typed partial changes: omitted fields stay, `null` clears a field,
 * and `properties` merges key by key.
 */
function applyChanges<Value extends object>(value: Value, changes: object): Value {
  const next = { ...value } as Record<string, unknown>;
  for (const [key, change] of Object.entries(changes)) {
    if (change === undefined) continue;
    if (key === "public") {
      next.public = applyChanges((value as { public: object }).public, change as object);
    } else if (key === "properties") {
      const current = (value as { properties?: Properties }).properties;
      const properties = mergeProperties(current, change as PropertiesChanges);
      if (properties) next.properties = properties;
      else delete next.properties;
    } else if (change === null) {
      delete next[key];
    } else {
      next[key] = change;
    }
  }
  return next as Value;
}

function mergeProperties(
  properties: Properties | undefined,
  changes: PropertiesChanges,
): Properties | undefined {
  const merged: Properties = { ...properties };
  for (const [key, value] of Object.entries(changes)) {
    if (value === null) delete merged[key];
    else merged[key] = value;
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}
